import WRAP from "WRAP-JS";
import {
  configSurfaceAnalysisDhFront,
  configSurfaceAnalysisDhGrandMaster,
  configSurfaceAnalysisLayerContour,
  configSurfaceAnalysisLayerFront,
  configSurfaceAnalysisLayerHiLo,
  configSurfaceAnalysisUpConversionController
} from "./config/ConfigSurfaceAnalysis";
import { _orderBy, formatBaseTime, formatValidTime, parseUTCDate } from "../../../utils/FPEUtils";
import LegendSurfaceAnalysis from "../legend/LegendSurfaceAnalysis";
import { WX_ID_SURFACEANALYSIS } from "../../../store/WeatherContentsState";

export const SURFACE_GRAND_MASTER = 'GrandMaster';
export const SURFACE_FRONT = 'FRONT';

class SurfaceAnalysis {

  static NAME = WX_ID_SURFACEANALYSIS;

  dhContourController;
  dhHiLo;
  dhFront;
  layerContour;
  layerHiLo;
  layerFront;
  timeInfo = {
    [SURFACE_GRAND_MASTER]: {
      baseTime: '---',
      validTime: '---'
    },
    [SURFACE_FRONT]: {
      baseTime: '---',
      validTime: '---'
    }
  }
  ready = false;
  etd;
  elapsed = 0;
  visible
  dispatcher;

  constructor() {
    this.layerContour = new WRAP.Geo.Layer('SURFACE_CONTOUR');
    this.layerHiLo = new WRAP.Geo.Layer('SURFACE_HILO');
    this.layerFront = new WRAP.Geo.Layer('SURFACE_FRONT');
    this.layerContour.setVisible(false);
    this.layerHiLo.setVisible(false);
    this.layerFront.setVisible(false);
    this.layerContour.disableInteraction(true);
    this.layerHiLo.disableInteraction(true);
    this.layerFront.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(allResolve => {

      let status = 'pending';

      this.dhHiLo = new WRAP.Data(configSurfaceAnalysisDhGrandMaster);
      this.dhFront = new WRAP.Data(configSurfaceAnalysisDhFront);
      this.dhContourController = new WRAP.Data(configSurfaceAnalysisUpConversionController);

      const grandMasterPromise = new Promise(resolve => {
        this.dhHiLo.updated('timelist', ref => {
          if (status === 'fulfilled') {
            this._selectBaseValidTime(ref, SURFACE_GRAND_MASTER);
          }
          resolve(ref);
        });
      });

      const frontPromise = new Promise(resolve => {
        this.dhFront.updated('timelist', ref => {
          if (status === 'fulfilled') {
            this._selectBaseValidTime(ref, SURFACE_FRONT);
          }
          resolve(ref);
        });
      });

      Promise.all([grandMasterPromise, frontPromise]).then(refs => {
        this._selectBaseValidTime(refs[0], SURFACE_GRAND_MASTER);
        this._selectBaseValidTime(refs[1], SURFACE_FRONT);
        allResolve(refs);
        status = 'fulfilled';
      });
    });
  }

  initLayer() {
    this.layerHiLo.configure(this.dhHiLo, configSurfaceAnalysisLayerHiLo);
    this.layerContour.configure(this.dhContourController, configSurfaceAnalysisLayerContour);
    this.layerFront.configure(this.dhFront, configSurfaceAnalysisLayerFront);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return [this.layerContour, this.layerHiLo, this.layerFront];
  }

  setVisible(visible) {

    if (!this.ready) return;

    if (visible) {
      this.layerContour.show();
      this.layerHiLo.show();
      this.layerFront.show();
    } else {
      this.layerContour.hide();
      this.layerHiLo.hide();
      this.layerFront.hide();
    }

    this.dhHiLo.setAutoUpdate('timelist', visible);
    this.dhFront.setAutoUpdate('timelist', visible);

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    const target = [SURFACE_GRAND_MASTER, SURFACE_FRONT].reduce((accumulator, type) => {
      const time = this.timeInfo[type];
      if (time.baseTime !== '---') {
        return accumulator.baseTime < time.baseTime ? time : accumulator
      }
      return accumulator;
    },{baseTime:'---', validTime:'---'});

    const baseTime = target.baseTime !== '---' ? formatBaseTime(target.baseTime) : target.baseTime;
    const validTime = target.validTime !== '---' ? formatValidTime(target.validTime) : target.validTime;

    return {
      id: SurfaceAnalysis.NAME,
      label: 'SurfaceAnalysis',
      baseTime: baseTime,
      validTime: validTime
    }
  }

  getLegend() {
    return {
      id: SurfaceAnalysis.NAME,
      label: 'S Analysis',
      component: LegendSurfaceAnalysis,
      contents: this
    };
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dhFront.retained.timelist, SURFACE_GRAND_MASTER);
    this._selectBaseValidTime(this.dhHiLo.retained.timelist, SURFACE_FRONT);

  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dhFront.retained.timelist, SURFACE_GRAND_MASTER);
    this._selectBaseValidTime(this.dhHiLo.retained.timelist, SURFACE_FRONT);
  }

  _updateDataset(dataType) {
    let time = this.timeInfo[dataType];

    if (time.baseTime !== '---' && time.validTime !== '---') {
      if (dataType === SURFACE_GRAND_MASTER) {
        this.layerContour.set({
          element: 'PRMSL',
          basetime: time.baseTime,
          validtime: time.validTime
        });

        this.layerHiLo.set({
          element: 'PRMSL',
          basetime: time.baseTime,
          validtime: time.validTime
        });

      } else {
        this.layerFront.set({
          basetime: time.baseTime,
          validtime: time.validTime
        });
      }
    }
  }

  _selectBaseValidTime(ref, dataType) {
    let timeList = ref;
    if (!timeList) return;
    timeList = this._syncTimeList(timeList.timelist, dataType);

    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      targetTime = Date.now();
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    //最新のbasetimeを取得
    const latestBasetime = _orderBy(timeList, ['basetime'], ['desc'])[0];
    let tmpTimeList = latestBasetime.validtime.map(validtime => {
      return {
        "validtime": validtime,
        "basetime": latestBasetime.basetime,
        "validtime_msec": parseUTCDate(validtime).getTime()
      };
    });

    tmpTimeList = this._syncValidTimeList(tmpTimeList, dataType);

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];
    const validTime = this.timeInfo[dataType].validTime;
    const baseTime =  this.timeInfo[dataType].baseTime;

    if (selectedTime && (validTime !== selectedTime.validtime || baseTime !== selectedTime.basetime)) {
      this.timeInfo[dataType].baseTime = selectedTime.basetime;
      this.timeInfo[dataType].validTime = selectedTime.validtime;
      this._updateDataset(dataType);
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _syncTimeList(timeList, dataType) {

    const target = dataType === SURFACE_GRAND_MASTER ? this.dhFront : this.dhHiLo;

    const baseList = target.retained.timelist.timelist.map(x => {
      return x.basetime
    });

    return timeList.filter(x => {
      return baseList.includes(x.basetime);
    });
  }

  _syncValidTimeList(validList, dataType) {

    const target = dataType === SURFACE_GRAND_MASTER ? this.dhFront : this.dhHiLo;

    const baseTime = validList[0].basetime;

    const validTimeList = target.retained.timelist.timelist.find(x => {
      return x.basetime === baseTime
    }).validtime;

    return validList.filter(x => {
      return validTimeList.includes(x.validtime);
    });
  }

  applyMapColor(mapColor) {
    const style = mapColor === "white" ? "default" : "dark";
    this.layerHiLo.setStyle(style);
    this.layerContour.setStyle(style);
  }
}

export default SurfaceAnalysis;